// app/travel-guides/[province]/page.tsx
"use client";

import React, { useState, useEffect, useMemo } from "react";
import {
  ArrowLeft,
  Search,
  MapPin,
  Star,
  Heart,
  ChevronRight,
  Filter,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useParams } from "next/navigation";
import { getProvinceById } from "../data/provinces";
import {
  getAttractionsByProvince,
  getDistrictsByProvince,
  searchAttractions,
  type Attraction,
} from "../data/attractions";

const ProvinceDetailPage = () => {
  const params = useParams();
  const provinceId = params.province as string;

  const [province, setProvince] = useState<any>(null);
  const [allAttractions, setAllAttractions] = useState<Attraction[]>([]);
  const [filteredAttractions, setFilteredAttractions] = useState<Attraction[]>(
    [],
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState<string>("");
  const [districts, setDistricts] = useState<string[]>([]);

  useEffect(() => {
    const foundProvince = getProvinceById(provinceId);
    const attractions = getAttractionsByProvince(provinceId);
    const provinceDistricts = getDistrictsByProvince(provinceId);

    setProvince(foundProvince);
    setAllAttractions(attractions);
    setFilteredAttractions(attractions);
    setDistricts(provinceDistricts);
  }, [provinceId]);

  // Filter attractions based on search and district
  useEffect(() => {
    let result = allAttractions;

    // Filter by district if selected
    if (selectedDistrict) {
      result = result.filter((a) => a.district === selectedDistrict);
    }

    // Filter by search term
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      result = result.filter(
        (attraction) =>
          attraction.title.toLowerCase().includes(lowerSearch) ||
          attraction.town.toLowerCase().includes(lowerSearch) ||
          attraction.district.toLowerCase().includes(lowerSearch) ||
          attraction.shortDescription.toLowerCase().includes(lowerSearch) ||
          attraction.category.toLowerCase().includes(lowerSearch),
      );
    }

    setFilteredAttractions(result);
  }, [searchTerm, selectedDistrict, allAttractions]);

  if (!province) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Province not found
          </h2>
          <p className="text-gray-600 mb-6">
            The province you're looking for doesn't exist.
          </p>
          <Link
            href="/travel-guides"
            className="bg-purple-600 hover:bg-purple-700 text-white dark:text-white dark:hover:bg-purple-700 px-6 py-3 rounded-lg font-medium inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Provinces
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link
          href="/travel-guides"
          className="inline-flex items-center gap-2 text-gray-800 hover:text-purple-600 transition-colors mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Back to All Provinces</span>
        </Link>

        <div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-3">
            {province.name}
          </h1>
          <p className="text-lg text-gray-700 mb-4">{province.description}</p>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span>{districts.length} Districts</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4" />
              <span>{allAttractions.length} Attractions</span>
            </div>
          </div>
        </div>
      </div>

      {/* Search & Filter Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* District Filter */}
            <div className="flex-1">
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={selectedDistrict}
                  onChange={(e) => setSelectedDistrict(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-700 appearance-none bg-white"
                >
                  <option value="">All Districts</option>
                  {districts.map((district) => (
                    <option key={district} value={district}>
                      {district}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Search Input */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search places, districts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <button
              onClick={() => {
                // Search is already handled by useEffect
              }}
              className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-lg font-medium transition-colors whitespace-nowrap"
            >
              Search
            </button>
          </div>
        </div>
      </div>

      {/* Results Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-600">
            Showing{" "}
            <span className="font-semibold">{filteredAttractions.length}</span>{" "}
            of <span className="font-semibold">{allAttractions.length}</span>{" "}
            attractions
            {selectedDistrict && <span> in {selectedDistrict}</span>}
          </p>
        </div>

        {/* Attractions Grid */}
        {filteredAttractions.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAttractions.map((attraction) => (
              <Link
                key={attraction.id}
                href={`/travel-guides/${provinceId}/${attraction.id}`}
                className="group"
              >
                <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden transform group-hover:-translate-y-1">
                  {/* Image */}
                  <div className="relative h-56 overflow-hidden">
                    <Image
                      src={attraction.image}
                      alt={attraction.title}
                      fill
                      className="object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    {/* <div className="absolute top-4 right-4">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        className="bg-white/90 hover:bg-white dark:bg-purple-700 dark:hover:bg-red-600 rounded-full p-2 transition-colors"
                      >
                        <Heart className="w-5 h-5 dark:text-gray-700" />
                      </button>
                    </div> */}
                    <div className="absolute bottom-4 left-4">
                      <span className="bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                        {attraction.category}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-purple-600 transition-colors">
                      {attraction.title}
                    </h3>

                    <div className="flex items-center text-gray-600 mb-3">
                      <MapPin className="w-4 h-4 mr-1 flex-shrink-0" />
                      <span className="text-sm">
                        {attraction.town}, {attraction.district}
                      </span>
                    </div>

                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {attraction.shortDescription}
                    </p>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      {/* <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        <span className="font-semibold text-gray-900">
                          {attraction.rating}
                        </span>
                        <span className="text-gray-500 text-sm">
                          ({attraction.reviews})
                        </span>
                      </div> */}

                      <div className="flex items-center text-purple-600 font-medium text-sm">
                        View Details
                        <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="text-gray-400 mb-4">
              <Search className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No attractions found
            </h3>
            <p className="text-gray-600 mb-6">
              Try adjusting your search or filter criteria
            </p>
            <button
              onClick={() => {
                setSearchTerm("");
                setSelectedDistrict("");
              }}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProvinceDetailPage;
